import "prismjs/themes/prism-tomorrow.css";

import React, { useEffect, useMemo } from 'react';

import Prism from 'prismjs';
import "prismjs/components/prism-json";

/**
 * Renders a webhook log's posted payload or its response.
 *
 * Both fields arrive as strings and neither is guaranteed to be JSON, so parsing is
 * defensive. The previous version did:
 *
 *   responsedata = Object.hasOwn(rs, "data_response") ? JSON.parse(rs?.data_response) : "";
 *
 * which had two failure modes, both reported:
 *
 *  1. BLANK WHITE SCREEN — `JSON.parse` throws on an empty string, on `undefined`, and on
 *     any non-JSON body (a plain "OK", an HTML error page from the receiving endpoint).
 *     The throw happens during render, so React unmounts the whole tree rather than just
 *     failing to pretty-print. Note `JSON.parse(null)` does NOT throw — it returns null —
 *     which is why this only showed up for some rows.
 *  2. EMPTY JSON BOX — a response stored as `'""'` parses to an empty string and rendered
 *     as a bare `""`, and an absent key rendered the same, so a missing response was
 *     indistinguishable from an empty one.
 *
 * Now: valid JSON is pretty-printed, non-JSON is shown verbatim (it is still the real
 * response), and genuinely empty is stated in words.
 */
const EMPTY_MESSAGE = 'No response recorded for this webhook delivery.';
const EMPTY_MESSAGE_POSTED = 'No payload recorded for this webhook delivery.';

function formatLogField(raw) {
	if (raw === null || raw === undefined) return { text: '', isEmpty: true };

	// Already an object (some callers hand over parsed JSON) — print it directly.
	if (typeof raw === 'object') {
		const isEmpty = Object.keys(raw).length === 0;
		return { text: isEmpty ? '' : JSON.stringify(raw, null, 2), isEmpty };
	}

	const text = String(raw).trim();
	if (!text) return { text: '', isEmpty: true };

	try {
		const parsed = JSON.parse(text);
		// `'""'`, `'null'`, `'{}'` and `'[]'` all parse successfully but carry nothing.
		const isEmpty =
			parsed === null ||
			parsed === '' ||
			(typeof parsed === 'object' && Object.keys(parsed).length === 0);
		return { text: isEmpty ? '' : JSON.stringify(parsed, null, 2), isEmpty };
	} catch {
		// Not JSON — a plain-text or HTML body. Show it as-is rather than crashing.
		return { text, isEmpty: false, isPlainText: true };
	}
}

const Veiw = ({ type, rs }) => {
	const field = type === 'veiw_data' ? rs?.data_posted : rs?.data_response;
	const { text, isEmpty, isPlainText } = useMemo(() => formatLogField(field), [field]);

	useEffect(() => {
		// Re-highlight when the content changes, not only on first mount.
		if (!isEmpty && !isPlainText) Prism.highlightAll();
	}, [text, isEmpty, isPlainText]);

	if (isEmpty) {
		return (
			<div style={{ padding: '16px', color: '#718096', fontSize: '14px' }}>
				{type === 'veiw_data' ? EMPTY_MESSAGE_POSTED : EMPTY_MESSAGE}
			</div>
		);
	}

	return (
		<pre style={{ backgroundColor: 'black', textShadow: 'none', color: 'white', padding: '0px', borderRadius: '8px' }}>
			<span style={{ padding: 15, backgroundColor: '#2F2F2F', fontSize: '14px', display: 'block', marginBottom: '10px' }}>
				{isPlainText ? 'text' : 'json'}
			</span>
			<code
				className={isPlainText ? undefined : 'language-json'}
				style={{ padding: '0px 15px 15px', display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
			>
				{text}
			</code>
		</pre>
	);
};


export default Veiw;
